import type { MigrationStore, StorageMigration, StorageMigrationResult } from './types';

/**
 * Applies migrations in strict version order, one step at a time.
 *
 * It never throws: a broken or missing step stops the chain and is reported, leaving storage
 * at the last version that completed. Losing a child's progress to an exception on boot is
 * worse than running an older schema.
 *
 * There is no rollback. A step that writes and then fails leaves those writes in place and the
 * recorded version unchanged, so the step runs again next launch — which is why
 * `StorageMigration.migrate` is required to be idempotent. Per-step checkpointing is deferred
 * to the first real migration (backlog BL-001).
 */
export function runStorageMigrations(
  store: MigrationStore,
  migrations: readonly StorageMigration[],
  currentVersion: number,
): StorageMigrationResult {
  const ordered = [...migrations].sort((a, b) => a.from - b.from);
  const applied: string[] = [];
  const errors: string[] = [];

  // Two migrations claiming the same starting version means one of them would silently never
  // run. Refuse the whole set rather than pick one and corrupt data by omission.
  const seenFrom = new Set<number>();
  for (const migration of ordered) {
    if (seenFrom.has(migration.from)) {
      return {
        fromVersion: currentVersion,
        toVersion: currentVersion,
        applied: [],
        errors: [
          `Duplicate migration for schema version ${migration.from} ("${migration.description}"); no migrations were run.`,
        ],
      };
    }
    seenFrom.add(migration.from);
  }

  let version = currentVersion;

  for (const migration of ordered) {
    if (migration.from < version) continue;

    if (migration.from !== version) {
      errors.push(
        `Migration gap: nothing upgrades schema version ${version} (next available starts at ${migration.from}).`,
      );
      break;
    }

    if (migration.to !== migration.from + 1) {
      errors.push(
        `Migration "${migration.description}" must step exactly one version (${migration.from} → ${migration.from + 1}).`,
      );
      break;
    }

    try {
      migration.migrate(store);
    } catch (cause) {
      errors.push(
        `Migration "${migration.description}" failed: ${cause instanceof Error ? cause.message : String(cause)}`,
      );
      break;
    }

    applied.push(migration.description);
    version = migration.to;
  }

  return { fromVersion: currentVersion, toVersion: version, applied, errors };
}
